/**
 * GWP Cart Validator
 * Prevents customers from manually adding GWP items to cart without qualifying
 * Automatically removes GWP products that are in cart without meeting threshold
 */

class GWPCartValidator {
  constructor(config) {
    this.gwpVariantIds = config.gwpVariantIds || [];
    this.cartThreshold = config.cartThreshold || 0;
    this.showOnlyWithDiscount = config.showOnlyWithDiscount || false;
    this.requiredDiscountCode = config.requiredDiscountCode || '';
    this.initialized = false;

    if (this.gwpVariantIds.length > 0) {
      this.init();
    }
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    // Validate cart on page load
    this.validateCart();

    // Listen for cart updates (from Ajax API, theme events, etc.)
    document.addEventListener('DOMContentLoaded', () => {
      this.validateCart();
      this.initCheckoutProtection();
    });

    // Listen for custom cart update events (many themes dispatch these)
    document.addEventListener('cart:updated', () => {
      this.validateCart();
      this.updateCheckoutButtonState();
    });

    // Monitor cart changes via storage event (for multi-tab scenarios)
    window.addEventListener('storage', (e) => {
      if (e.key === 'cart' || e.key === 'cart-updated') {
        this.validateCart();
      }
    });

    // Poll cart every 5 seconds as fallback (lightweight check)
    this.startPolling();
  }

  startPolling() {
    this.pollInterval = setInterval(() => {
      this.validateCart();
    }, 5000);
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  }

  async validateCart() {
    try {
      // Fetch current cart state
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Check for unauthorized GWP items
      const unauthorizedItems = this.findUnauthorizedGWPItems(cart);

      if (unauthorizedItems.length > 0) {
        console.log('[GWP Validator] Found unauthorized GWP items:', unauthorizedItems);
        await this.removeUnauthorizedItems(unauthorizedItems);
      }

      // Update checkout button state
      this.updateCheckoutButtonState();
    } catch (error) {
      console.error('[GWP Validator] Error validating cart:', error);
    }
  }

  findUnauthorizedGWPItems(cart) {
    const unauthorizedItems = [];

    // Check if customer qualifies for GWP
    const isQualified = this.checkQualification(cart);

    // If not qualified, all GWP items are unauthorized
    if (!isQualified) {
      cart.items.forEach(item => {
        if (this.gwpVariantIds.includes(item.variant_id)) {
          unauthorizedItems.push({
            key: item.key,
            variant_id: item.variant_id,
            title: item.title,
            quantity: item.quantity
          });
        }
      });
    }

    return unauthorizedItems;
  }

  checkQualification(cart) {
    // Calculate cart total (excluding GWP items to prevent circular logic)
    let cartTotalWithoutGWP = 0;
    cart.items.forEach(item => {
      if (!this.gwpVariantIds.includes(item.variant_id)) {
        cartTotalWithoutGWP += item.line_price;
      }
    });

    // Check cart threshold
    const meetsThreshold = cartTotalWithoutGWP >= this.cartThreshold;

    // Check discount code requirement (if applicable)
    let hasRequiredDiscount = true;
    if (this.showOnlyWithDiscount && this.requiredDiscountCode) {
      hasRequiredDiscount = this.checkDiscountCode(cart);
    }

    return meetsThreshold && hasRequiredDiscount;
  }

  checkDiscountCode(cart) {
    if (!this.requiredDiscountCode) return true;

    const requiredCodeUpper = this.requiredDiscountCode.toUpperCase();

    // Check cart.discount_codes (API v1)
    if (cart.discount_codes && cart.discount_codes.length > 0) {
      for (const discount of cart.discount_codes) {
        if (discount.code && discount.code.toUpperCase() === requiredCodeUpper) {
          return true;
        }
      }
    }

    // Check cart.cart_level_discount_applications (API v2)
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
      for (const discount of cart.cart_level_discount_applications) {
        if (discount.title && discount.title.toUpperCase() === requiredCodeUpper) {
          return true;
        }
      }
    }

    return false;
  }

  async removeUnauthorizedItems(items) {
    try {
      // Build updates object to set quantities to 0
      const updates = {};
      items.forEach(item => {
        updates[item.key] = 0;
      });

      // Update cart via Shopify Cart API
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        const updatedCart = await response.json();
        console.log('[GWP Validator] Removed unauthorized GWP items successfully');

        // Show user notification
        this.showNotification(items);

        // Trigger cart refresh event
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: updatedCart }));

        // Reload page to reflect changes (optional - remove if your theme handles updates)
        if (window.location.pathname.includes('/cart')) {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      } else {
        console.error('[GWP Validator] Failed to remove items:', response.statusText);
      }
    } catch (error) {
      console.error('[GWP Validator] Error removing unauthorized items:', error);
    }
  }

  /**
   * Initialize checkout protection
   * Intercepts checkout button clicks and validates cart before allowing checkout
   */
  initCheckoutProtection() {
    // Find all checkout buttons and forms
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], ' +
      'input[name="checkout"], ' +
      'a[href*="/checkout"], ' +
      '.cart__checkout, ' +
      '[data-testid="Checkout-button"]'
    );

    const checkoutForms = document.querySelectorAll('form[action*="/cart"]');

    // Intercept button clicks
    checkoutButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        const hasUnauthorized = await this.checkForUnauthorizedItems();

        if (hasUnauthorized) {
          e.preventDefault();
          e.stopPropagation();
          this.showCheckoutBlockedNotification();
          return false;
        }
      }, true); // Use capture phase to intercept before other handlers
    });

    // Intercept form submissions
    checkoutForms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        // Check if submitting to checkout
        const formAction = form.action || '';
        const hasCheckoutParam = new URLSearchParams(new FormData(form)).has('checkout');

        if (formAction.includes('/cart') && hasCheckoutParam) {
          const hasUnauthorized = await this.checkForUnauthorizedItems();

          if (hasUnauthorized) {
            e.preventDefault();
            e.stopPropagation();
            this.showCheckoutBlockedNotification();
            return false;
          }
        }
      }, true);
    });

    // Add disabled button styles
    this.injectDisabledButtonStyles();

    // Initial button state check
    this.updateCheckoutButtonState();

    console.log('[GWP Validator] Checkout protection initialized');
  }

  /**
   * Update checkout button state based on cart validation
   */
  async updateCheckoutButtonState() {
    const hasUnauthorized = await this.checkForUnauthorizedItems();

    // Find all checkout buttons
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], ' +
      'input[name="checkout"], ' +
      '.cart__checkout:not(a), ' +
      '[data-testid="Checkout-button"]'
    );

    const checkoutLinks = document.querySelectorAll(
      'a[href*="/checkout"], ' +
      'a.cart__checkout'
    );

    if (hasUnauthorized) {
      // Disable buttons
      checkoutButtons.forEach(button => {
        button.disabled = true;
        button.classList.add('gwp-disabled');
        button.setAttribute('data-gwp-disabled', 'true');
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('title', 'Remove gift items to checkout');
      });

      // Disable links by preventing default and adding disabled class
      checkoutLinks.forEach(link => {
        link.classList.add('gwp-disabled');
        link.setAttribute('data-gwp-disabled', 'true');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('title', 'Remove gift items to checkout');
        link.style.pointerEvents = 'none';
      });

      // Add warning message if not already present
      this.addCheckoutWarningMessage();
    } else {
      // Enable buttons
      checkoutButtons.forEach(button => {
        button.disabled = false;
        button.classList.remove('gwp-disabled');
        button.removeAttribute('data-gwp-disabled');
        button.removeAttribute('aria-disabled');
        button.removeAttribute('title');
      });

      // Enable links
      checkoutLinks.forEach(link => {
        link.classList.remove('gwp-disabled');
        link.removeAttribute('data-gwp-disabled');
        link.removeAttribute('aria-disabled');
        link.removeAttribute('title');
        link.style.pointerEvents = '';
      });

      // Remove warning message
      this.removeCheckoutWarningMessage();
    }
  }

  /**
   * Add warning message near checkout button
   */
  addCheckoutWarningMessage() {
    // Only add on cart page
    if (!window.location.pathname.includes('/cart')) return;

    // Check if message already exists
    if (document.querySelector('.gwp-checkout-warning')) return;

    // Find checkout button container
    const checkoutButton = document.querySelector(
      'button[name="checkout"], ' +
      'input[name="checkout"], ' +
      '.cart__checkout'
    );

    if (checkoutButton) {
      const warning = document.createElement('div');
      warning.className = 'gwp-checkout-warning';
      warning.innerHTML = `
        <div class="gwp-checkout-warning__content">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <p>Remove unauthorized gift items to proceed to checkout</p>
        </div>
      `;

      // Insert after checkout button
      checkoutButton.parentElement.insertBefore(warning, checkoutButton.nextSibling);
    }
  }

  /**
   * Remove checkout warning message
   */
  removeCheckoutWarningMessage() {
    const warning = document.querySelector('.gwp-checkout-warning');
    if (warning) {
      warning.remove();
    }
  }

  /**
   * Inject styles for disabled checkout buttons
   */
  injectDisabledButtonStyles() {
    if (document.getElementById('gwp-disabled-button-styles')) return;

    const style = document.createElement('style');
    style.id = 'gwp-disabled-button-styles';
    style.textContent = `
      /* Disabled checkout button styles */
      button.gwp-disabled,
      input.gwp-disabled,
      a.gwp-disabled {
        opacity: 0.5 !important;
        cursor: not-allowed !important;
        background-color: #cccccc !important;
        color: #666666 !important;
        border-color: #cccccc !important;
        position: relative !important;
      }

      button.gwp-disabled:hover,
      input.gwp-disabled:hover,
      a.gwp-disabled:hover {
        opacity: 0.5 !important;
        transform: none !important;
        background-color: #cccccc !important;
      }

      /* Tooltip on hover */
      button.gwp-disabled::after,
      input.gwp-disabled::after,
      a.gwp-disabled::after {
        content: attr(title);
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%) translateY(-8px);
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 13px;
        white-space: nowrap;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease;
        z-index: 1000;
      }

      button.gwp-disabled:hover::after,
      input.gwp-disabled:hover::after,
      a.gwp-disabled:hover::after {
        opacity: 1;
      }

      /* Warning message styles */
      .gwp-checkout-warning {
        margin-top: 12px;
        padding: 12px 16px;
        background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
        border-left: 4px solid #ff9800;
        border-radius: 4px;
        animation: gwp-warning-fade-in 0.3s ease-out;
      }

      .gwp-checkout-warning__content {
        display: flex;
        align-items: flex-start;
        gap: 12px;
      }

      .gwp-checkout-warning__content svg {
        flex-shrink: 0;
        color: #ff9800;
        margin-top: 2px;
      }

      .gwp-checkout-warning__content p {
        margin: 0;
        font-size: 14px;
        line-height: 1.5;
        color: #856404;
        font-weight: 500;
      }

      @keyframes gwp-warning-fade-in {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Mobile responsive */
      @media (max-width: 768px) {
        .gwp-checkout-warning {
          margin-top: 16px;
        }

        button.gwp-disabled::after,
        input.gwp-disabled::after,
        a.gwp-disabled::after {
          display: none; /* Hide tooltip on mobile, use warning message instead */
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Check if cart has unauthorized GWP items (async version for checkout blocking)
   */
  async checkForUnauthorizedItems() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const unauthorizedItems = this.findUnauthorizedGWPItems(cart);

      return unauthorizedItems.length > 0;
    } catch (error) {
      console.error('[GWP Validator] Error checking cart for checkout:', error);
      // On error, allow checkout (fail open to prevent false positives)
      return false;
    }
  }

  /**
   * Show notification when checkout is blocked
   */
  showCheckoutBlockedNotification() {
    const notification = document.createElement('div');
    notification.className = 'gwp-validator-notification gwp-validator-notification--error';
    notification.innerHTML = `
      <div class="gwp-validator-notification__content">
        <p><strong>⚠️ Checkout Blocked</strong></p>
        <p>Please remove gift items from your cart before checking out. Gift items can only be obtained through our promotion.</p>
        <button class="gwp-validator-notification__dismiss" onclick="this.closest('.gwp-validator-notification').remove()">
          Dismiss
        </button>
      </div>
    `;

    document.body.appendChild(notification);

    // Add error notification styles
    if (!document.getElementById('gwp-validator-styles-error')) {
      const style = document.createElement('style');
      style.id = 'gwp-validator-styles-error';
      style.textContent = `
        .gwp-validator-notification--error {
          background: linear-gradient(135deg, #ff4444 0%, #cc0000 100%);
          color: white;
          animation: gwp-shake 0.5s ease-in-out;
        }

        .gwp-validator-notification--error .gwp-validator-notification__content p {
          color: white;
        }

        .gwp-validator-notification__dismiss {
          margin-top: 12px;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 4px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .gwp-validator-notification__dismiss:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
        }

        @keyframes gwp-shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
      `;
      document.head.appendChild(style);
    }

    // Scroll to top to ensure user sees the notification
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.style.animation = 'gwp-slide-in 0.3s ease-in reverse';
        setTimeout(() => {
          notification.remove();
        }, 300);
      }
    }, 10000);
  }

  showNotification() {
    // Create a simple notification to inform the user
    const notification = document.createElement('div');
    notification.className = 'gwp-validator-notification';
    notification.innerHTML = `
      <div class="gwp-validator-notification__content">
        <p><strong>Gift item removed</strong></p>
        <p>Gift items can only be added when you meet the promotion requirements.</p>
      </div>
    `;

    document.body.appendChild(notification);

    // Style the notification (only add if not already present)
    if (!document.getElementById('gwp-validator-styles')) {
      const style = document.createElement('style');
      style.id = 'gwp-validator-styles';
      style.textContent = `
      .gwp-validator-notification {
        position: fixed;
        top: 20px;
        right: 20px;
        background: rgb(var(--mutant-color-yellow, 255, 205, 0));
        color: rgb(var(--mutant-color-black, 0, 0, 0));
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        max-width: 320px;
        animation: gwp-slide-in 0.3s ease-out;
      }

      .gwp-validator-notification__content p {
        margin: 0;
        font-size: 14px;
        line-height: 1.4;
      }

      .gwp-validator-notification__content p:first-child {
        font-weight: 600;
        margin-bottom: 4px;
      }

      @keyframes gwp-slide-in {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      @media (max-width: 768px) {
        .gwp-validator-notification {
          top: 10px;
          right: 10px;
          left: 10px;
          max-width: none;
        }
      }
    `;
      document.head.appendChild(style);
    }

    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      notification.style.animation = 'gwp-slide-in 0.3s ease-in reverse';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 5000);
  }

  destroy() {
    this.stopPolling();
    this.initialized = false;
  }
}

// Auto-initialize if config is provided via data attribute
document.addEventListener('DOMContentLoaded', () => {
  const configElement = document.getElementById('gwp-validator-config');

  if (configElement) {
    try {
      const config = JSON.parse(configElement.textContent);
      window.gwpCartValidator = new GWPCartValidator(config);
    } catch (error) {
      console.error('[GWP Validator] Error parsing config:', error);
    }
  }
});
